import { createApplicationService } from "./base/applicationServiceBase";
import { AccountsAPI } from "api/axios.config";
import { useMemo, useState, useEffect } from "react";
import { NominalSubtypes } from "components/Enums/Nominals.ts";

//-------------------------------------------
//           API Functions
//-------------------------------------------

const NOMINALS_BASE = "/nominals";

const fetchAllActiveNominalsAPI = () =>
  AccountsAPI.get(`${NOMINALS_BASE}?active=true`);

const addNewNominalAPI = (data) =>
  AccountsAPI.post(NOMINALS_BASE, JSON.stringify({ data }), {
    headers: { "Content-Type": "application/json" },
  });

const updateNominalAPI = (id, data) =>
  AccountsAPI.patch(`${NOMINALS_BASE}/${id}`, JSON.stringify({ data }), {
    headers: { "Content-Type": "application/json" },
  });

// Get single nominal by ID
const getViewNominalAPI = (id) => AccountsAPI.get(`${NOMINALS_BASE}/${id}`);

// Get nominal balances
const getNominalBalancesAPI = (nominalRef, FromDate, ToDate) =>
  AccountsAPI.get(
    `${NOMINALS_BASE}/Ref/${nominalRef}/Balances?FromDate=${FromDate}&ToDate=${ToDate}`,
  );

const getNominalByRefAPI = (nominalRef) =>
  AccountsAPI.get(`${NOMINALS_BASE}/Ref/${nominalRef}`);

const getActiveIncomeNominalsAPI = () =>
  AccountsAPI.get(
    `${NOMINALS_BASE}?banks=false&ExcludeArchived=true&Orderby=subType,nominalRef`,
  );

const getNominalsByTypeAPI = (type) =>
  AccountsAPI.get(`${NOMINALS_BASE}?type=${type}&active=true`);

const getPaymentFileFormatsAPI = () =>
  AccountsAPI.get(`${NOMINALS_BASE}/PaymentFileFormat`);

//-------------------------------------------
//-------------------------------------------

const nominalsService = createApplicationService({
  name: "nominals",
  apiFetchFn: fetchAllActiveNominalsAPI,
  apiAddFn: addNewNominalAPI,
  apiUpdateFn: updateNominalAPI,
});

//-------------------------------------------
//           Reducer
//-------------------------------------------

const nominalsReducer = nominalsService.reducer;

//-------------------------------------------
//           Core Functions
//-------------------------------------------

const getAllNominals = nominalsService.getData;
const addNominal = nominalsService.addData;
const updateNominal = nominalsService.updateData;
const useAllNominals = nominalsService.useData;
const resetNominals = nominalsService.resetData;

// Additional service functions
const getViewNominal = async (id) => {
  if (!id) {
    console.error("getViewNominal: No ID provided");
    return { success: false, error: "No ID provided" };
  }

  try {
    const response = await getViewNominalAPI(id);
    // Check if data is nested in response.data.data
    const data = response.data?.data || response.data;
    return { success: true, data: data };
  } catch (error) {
    console.error("Error fetching nominal:", error);
    return { success: false, error };
  }
};

const getNominalBalances = async (nominalRef, FromDate, ToDate) => {
  try {
    const response = await getNominalBalancesAPI(nominalRef, FromDate, ToDate);
    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error fetching nominal balances:", error);
    return { success: false, error };
  }
};

const getNominalsByRef = async (nominalRef) => {
  try {
    const response = await getNominalByRefAPI(nominalRef);
    return response;
  } catch (error) {
    console.error("Error fetching nominal by ref:", error);
    throw error;
  }
};

const getActiveIncomeNominals = async () => {
  try {
    const response = await getActiveIncomeNominalsAPI();
    return response;
  } catch (error) {
    console.error("Error fetching active income nominals:", error);
    throw error;
  }
};

const getNominalsByType = async (type) => {
  try {
    const response = await getNominalsByTypeAPI(type);
    return response;
  } catch (error) {
    console.error("Error fetching nominals by type:", error);
    throw error;
  }
};

// Payment file formats change rarely, so cache the in-flight/resolved request
// at module level instead of refetching every time a nominal dialog opens.
let paymentFileFormatsPromise = null;

const getPaymentFileFormats = async () => {
  if (!paymentFileFormatsPromise) {
    paymentFileFormatsPromise = getPaymentFileFormatsAPI()
      .then(({ data }) => {
        const raw = data?.data ?? data;
        return Array.isArray(raw) ? raw : raw ? [raw] : [];
      })
      .catch((error) => {
        paymentFileFormatsPromise = null;
        throw error;
      });
  }

  try {
    const data = await paymentFileFormatsPromise;
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching payment file formats:", error);
    return { success: false, error, data: [] };
  }
};

const usePaymentFileFormats = () => {
  const [state, setState] = useState({ data: [], loading: true });

  useEffect(() => {
    let cancelled = false;
    getPaymentFileFormats().then(({ data }) => {
      if (!cancelled) setState({ data, loading: false });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};

// Get nominals list with parameters (for search/filtering) - just API calls
const getNominalsList = async (parameters = "") => {
  try {
    let url = `/nominals?`;
    if (parameters !== "") {
      url += `${parameters}`;
    }
    const response = await AccountsAPI.get(url);
    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error fetching nominals list:", error);
    return { success: false, error };
  }
};

//-------------------------------------------
//           Filtered Hook (Client-side filtering)
//-------------------------------------------

const useNominalsBySubType = (subType, options = {}) => {
  const { excludeArchived = false } = options;
  const { data, loading } = useAllNominals();

  const filteredData = useMemo(() => {
    let list = data ?? [];
    if (excludeArchived) {
      list = list.filter((item) => item.archived !== true);
    }
    if (subType) {
      list = list.filter((item) => item.subType === subType);
    }
    return list;
  }, [data, subType, excludeArchived]);

  return { data: filteredData, loading };
};

const useNominalsByType = (type, options = {}) => {
    const { excludeArchived = false } = options;
    const { data, loading } = useAllNominals();

    const filteredData = useMemo(() => {
        let list = data ?? [];
        if (excludeArchived) {
            list = list.filter((item) => item.archived !== true);
        }
        if (type) {
            list = list.filter((item) => item.type === type);
        }
        return list;
    }, [data, type, excludeArchived]);

    return { data: filteredData, loading };
};

const useNonControlNominals = () => {
  const { data, loading } = useAllNominals();

  const filteredData = useMemo(() => {
    return (data ?? []).filter(
      (item) => item.control === false && item.archived !== true,
    );
  }, [data]);

  return { data: filteredData, loading };
};

const ACTIVE_BANK_SUBTYPES = new Set([
  NominalSubtypes.CLIENT_BANK,
  NominalSubtypes.OFFICE_BANK,
  NominalSubtypes.DDA_BANK,
]);

const filterActiveBankNominals = (list) => {
  const src = list ?? [];
  if (!src.length) return [];
  return src
    .filter(
      (item) =>
        item?.archived !== true && ACTIVE_BANK_SUBTYPES.has(item?.subType),
    )
    .sort((a, b) => {
      const bySub = (a.subType || "").localeCompare(b.subType || "");
      if (bySub !== 0) return bySub;
      return (a.nominalRef || "").localeCompare(b.nominalRef || "");
    });
};

const filterBatchPostingNominals = (
  list,
  { includeBalanceSheetOther = false } = {},
) =>
  (list ?? []).filter(
    (item) =>
      item?.archived !== true &&
      (item?.type === "ProfitAndLoss" ||
        (includeBalanceSheetOther &&
          item?.subType === NominalSubtypes.BS_OTHER)),
  );

const getActiveBanks = async () => {
  const all = await getAllNominals();
  return filterActiveBankNominals(all);
};

const useActiveBanks = () => {
  const { data, loading } = useAllNominals();
  const filteredData = useMemo(() => filterActiveBankNominals(data), [data]);
  return { data: filteredData, loading };
};

//-------------------------------------------
//           Reset Functions
//-------------------------------------------

// Function to reset nominals service (useful when nominals are updated)
const resetAllNominals = () => {
  resetNominals();
};

//-------------------------------------------
//           Exports
//-------------------------------------------

export {
  // Single reducer
  nominalsReducer,

  // Core functions
  getAllNominals,
  addNominal,
  updateNominal,
  useAllNominals,
  getViewNominal,
  getNominalBalances,
  getNominalsByRef,
  getActiveIncomeNominals,
  getNominalsByType,
  getNominalsList,
  usePaymentFileFormats,

  // Single filtered hook for UI components (client-side filtering)
  useNominalsByType,
  useNominalsBySubType,
  useNonControlNominals,
  getActiveBanks,
  useActiveBanks,
  filterBatchPostingNominals,

  // Reset functions
  resetNominals,
  resetAllNominals,
};
